import { ActionIcon, Box, Button, Drawer, Group, Text, Tooltip } from '@mantine/core';
import { IconArrowsMaximize, IconPlus, IconSparkles } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, izvedi, podatki } from '../api/odjemalec';
import { PomocIkona } from '../pomoc/PomocIkona';
import { KLJUC_ASISTENT } from './asistent';
import { PogovorAsistenta } from './PogovorAsistenta';

/**
 * Gumb »Asistent« v glavi in predal s pogovorom ob desnem robu, dostopen z vsakega zaslona; pogovor ostane odprt med
 * premikanjem po aplikaciji. »Razširi« odpre isti pogovor na strani /asistent. Na strani /asistent gumba ni.
 * Predstavitev: ob prvi prijavi po uvedbi se predal odpre sam, enkrat na uporabnika (strežnik si zapomni, da ga je
 * videl – na vseh napravah; med prijavo skrbnika kot uporabnik ne).
 */
export function AsistentPredal() {
  const [odprto, setOdprto] = useState(false);
  const [pogovorId, setPogovorId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const predstavitev = useQuery({
    queryKey: [...KLJUC_ASISTENT, 'predstavitev'],
    queryFn: ({ signal }) => podatki(api.GET('/api/asistent/predstavitev', { signal })),
    staleTime: Infinity,
  });
  const predstavljeno = useRef(false);
  const naStrani = pathname.startsWith('/asistent');
  useEffect(() => {
    if (predstavitev.data?.prikazi && !predstavljeno.current && !naStrani) {
      predstavljeno.current = true;
      setOdprto(true);
      void izvedi(api.POST('/api/asistent/predstavitev')).catch(() => {
        // ni usodno: naslednjič se odpre še enkrat
      });
    }
  }, [predstavitev.data, naStrani]);
  if (naStrani) {
    return null;
  }
  return (
    <>
      <Button
        size="compact-md"
        variant="white"
        color="dark"
        leftSection={<IconSparkles size={16} />}
        onClick={() => setOdprto(true)}
        aria-label="Asistent"
        visibleFrom="sm"
      >
        Asistent
      </Button>
      <ActionIcon
        size="lg"
        variant="white"
        color="dark"
        onClick={() => setOdprto(true)}
        aria-label="Asistent"
        hiddenFrom="sm"
      >
        <IconSparkles size={18} />
      </ActionIcon>
      <Drawer
        opened={odprto}
        onClose={() => setOdprto(false)}
        position="right"
        size="lg"
        padding="sm"
        title={
          <Group gap={8} wrap="nowrap">
            <IconSparkles size={20} />
            <Text fw={700} size="lg">
              Asistent
            </Text>
          </Group>
        }
        styles={{
          content: { display: 'flex', flexDirection: 'column' },
          body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
          title: { flex: 1 },
        }}
      >
        <Group gap={4} justify="flex-end" mt={-8} mb={4}>
          <Tooltip label="Nov pogovor">
            <ActionIcon variant="subtle" color="gray" aria-label="Nov pogovor" onClick={() => setPogovorId(null)}>
              <IconPlus size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Razširi (vsi pogovori)">
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Razširi"
              onClick={() => {
                setOdprto(false);
                void navigate(pogovorId === null ? '/asistent' : `/asistent/${pogovorId}`);
              }}
            >
              <IconArrowsMaximize size={18} />
            </ActionIcon>
          </Tooltip>
          <PomocIkona clanek="asistent" />
        </Group>
        <Box style={{ flex: 1, minHeight: 0 }}>
          <PogovorAsistenta pogovorId={pogovorId} naPogovor={setPogovorId} />
        </Box>
      </Drawer>
    </>
  );
}
